import React, { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Alert,
  Autocomplete,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  Tooltip,
} from '@mui/material';
import './AdminPage.css';
import {
  AdminBook,
  AdminBookRequest,
  AdminCatalogItem,
  AdminOrder,
  AdminOrderItem,
  AdminUser,
  AdminUserRequest,
  BookPriceType,
  createAdminBook,
  createAdminOrder,
  createAdminUser,
  deleteAdminBook,
  deleteAdminBookPermanently,
  getAdminBook,
  getAdminBooks,
  getAdminUsers,
  getCatalogItems,
  getOrders,
  getOrdersByUser,
  importBooksFromCsv,
  OrderStatus,
  updateAdminBook,
  updateAdminUser,
  updateOrderItems,
  updateOrderStatus,
  uploadBookImage,
  UserRole,
  UserStatus,
  AdminCollection,
  AdminCollectionItemRequest,
  getAdminCollections,
  createAdminCollection,
  updateAdminCollection,
  deleteAdminCollection,
  AdminWaitlistEntry,
  AdminWaitlistItem,
  WaitlistSource,
  getAdminWaitlists,
  getAdminWaitlist,
  addAdminWaitlistEntry,
  updateAdminWaitlistEntry,
  removeAdminWaitlistEntry,
  reorderAdminWaitlist,
} from '../../common/Rest/Services/AdminService/AdminService';
import { ImageData } from '../../common/Rest/Services/ItemService/Data';
import { DeliveryInformationData } from '../../common/Rest/Services/UserService/Data';
import publishersServiceInstance, { Publisher } from '../../common/Rest/Services/PublishersService';
import genresServiceInstance, { Genre } from '../../common/Rest/Services/GenresService';
import tagsServiceInstance, { Tag } from '../../common/Rest/Services/TagsService';
import authorsServiceInstance, { Author } from '../../common/Rest/Services/AuthorsService';
import seriesServiceInstance, { Series } from '../../common/Rest/Services/SeriesService';
import NovaPoshtaAddressFields from '../../common/Delivery/NovaPoshtaAddressFields';
import SelfPickupFields from '../../common/Delivery/SelfPickupFields';
import DeliveryProviderIcon from '../../common/Delivery/DeliveryProviderIcon';
import {
  clearDeliveryTarget,
  clearProviderDetails,
  destinationLabel,
  emptyDelivery,
  providerLabel,
  renderProviderValue,
  toDeliveryPayload,
} from '../../common/Delivery/deliveryForm';
import { DeliveryDestination, DeliveryProvider } from '../../common/Rest/Services/UserService/Data';
import ROUTES from '../../common/Routing/Routes';
import { toAbsoluteUrl } from '../../common/Utils/BookImages';
import { getBookImageUrl } from '../../common/Utils/BookImages';
import { ApiError } from '../../common/Rest/ReaderlyHttpClient';
import AccountInviteBox from './AccountInviteBox';
import AdminShopPanel from './AdminShopPanel';
import AdminShopInquiries from './AdminShopInquiries';

type AdminTab = 'orders' | 'books' | 'images' | 'users' | 'import' | 'collections' | 'waitlist' | 'shop';

const getAdminBookIdParam = (searchParams: URLSearchParams) => {
  const value = Number(searchParams.get('bookId'));
  return Number.isFinite(value) && value > 0 ? value : null;
};

const getAdminTabParam = (searchParams: URLSearchParams): AdminTab => {
  const tab = searchParams.get('tab');
  if (tab === 'orders' || tab === 'books' || tab === 'images' || tab === 'users' || tab === 'import' || tab === 'collections' || tab === 'waitlist' || tab === 'shop') {
    return tab;
  }

  return getAdminBookIdParam(searchParams) ? 'books' : 'orders';
};

const csvImportTemplateHeader = 'Name,Author,Publisher,ISBN,Pages,Rating,Price1,IsLimited,Tag,IsSeria,Description';
const csvImportTemplateExample =
  'Кобзар,Тарас Шевченко,Либідь,9789660690001,300,4.8,150,0,Класика,0,Збірка поезій';
const csvImportTemplate = `${csvImportTemplateHeader}\n${csvImportTemplateExample}\n`;

const roleLabels: Record<UserRole, string> = {
  [UserRole.Client]: 'Клієнт',
  [UserRole.Admin]: 'Адмін',
  [UserRole.Seller]: 'Продавець',
};

const statusUserLabels: Record<UserStatus, string> = {
  [UserStatus.Active]: 'Активний',
  [UserStatus.Block]: 'Заблокований',
  [UserStatus.Deleted]: 'Видалений',
};

const orderStatuses = [
  OrderStatus.Created,
  OrderStatus.NeedsClarification,
  OrderStatus.Paid,
  OrderStatus.Shipped,
  OrderStatus.Returning,
  OrderStatus.Completed,
  OrderStatus.Cancelled,
];

const statusLabels: Record<OrderStatus, string> = {
  [OrderStatus.Created]: 'Нове',
  [OrderStatus.NeedsClarification]: 'Потрібне уточнення',
  [OrderStatus.Paid]: 'Оплачено',
  [OrderStatus.Complectation]: 'Комплектація',
  [OrderStatus.Packed]: 'Запаковано',
  [OrderStatus.Shipped]: 'Відправлено',
  [OrderStatus.Returning]: 'Повернення',
  [OrderStatus.Completed]: 'Завершено',
  [OrderStatus.Cancelled]: 'Скасовано',
};

const money = (value: number) => `${Number(value || 0).toFixed(2)} грн`;

const normalize = (value: string | number | undefined | null) =>
  String(value ?? '').trim().toLowerCase();

// Admin panels reuse a single status message string for both success and
// failure feedback; these are the prefixes used for the failure case.
const isErrorMessage = (message: string) =>
  message.startsWith('Не') ||
  message.startsWith('Виберіть') ||
  message.startsWith('Вкажіть') ||
  message.startsWith('Додайте') ||
  message.startsWith('Заповніть');

const formatDate = (value: string) =>
  new Date(value).toLocaleString('uk-UA', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

interface OrderItemDraft {
  itemId: string;
  count: string;
}

interface BookDraft {
  name: string;
  description: string;
  isbn: string;
  pages: string;
  rating: string;
  internalRating: string;
  publisherId: string;
  seriesId: string;
  newSeriesName: string;
  seriesNumber: string;
  isLimitedEdition: boolean;
  stockCount: string;
  price: string;
  priceType: BookPriceType;
  genreIds: number[];
  tagIds: number[];
  authorIds: number[];
  newAuthorNames: string;
}

const emptyBookDraft: BookDraft = {
  name: '',
  description: '',
  isbn: '',
  pages: '0',
  rating: '',
  internalRating: '',
  publisherId: '',
  seriesId: '',
  newSeriesName: '',
  seriesNumber: '',
  isLimitedEdition: false,
  stockCount: '1',
  price: '0',
  priceType: BookPriceType.Rental,
  genreIds: [],
  tagIds: [],
  authorIds: [],
  newAuthorNames: '',
};

const toBookDraft = (book: AdminBook): BookDraft => ({
  name: book.name ?? '',
  description: book.description ?? '',
  isbn: book.isbn ?? '',
  pages: String(book.pages ?? 0),
  rating: book.rating == null ? '' : String(book.rating),
  internalRating: book.internalRating == null ? '' : String(book.internalRating),
  publisherId: book.publisherId ? String(book.publisherId) : '',
  seriesId: book.seriesId ? String(book.seriesId) : '',
  newSeriesName: '',
  seriesNumber: book.seriesNumber == null ? '' : String(book.seriesNumber),
  isLimitedEdition: book.isLimitedEdition ?? false,
  stockCount: String(book.stockCount ?? 0),
  price: String(book.price ?? 0),
  priceType: book.priceType ?? BookPriceType.Rental,
  genreIds: (book.genres ?? []).map((genre) => genre.id),
  tagIds: (book.tags ?? []).map((tag) => tag.id),
  authorIds: (book.authors ?? []).map((author) => author.id),
  newAuthorNames: '',
});

const optionalNumber = (value: string) => {
  if (value.trim() === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toBookRequest = (draft: BookDraft): AdminBookRequest => ({
  name: draft.name.trim(),
  description: draft.description.trim(),
  isbn: draft.isbn.trim(),
  pages: Math.max(0, Number(draft.pages) || 0),
  rating: optionalNumber(draft.rating),
  internalRating: optionalNumber(draft.internalRating),
  publisherId: Number(draft.publisherId) || undefined,
  seriesId: draft.seriesId ? Number(draft.seriesId) : undefined,
  newSeriesName: draft.seriesId ? undefined : draft.newSeriesName.trim() || undefined,
  seriesNumber: draft.seriesId || draft.newSeriesName.trim() ? optionalNumber(draft.seriesNumber) : null,
  isLimitedEdition: draft.isLimitedEdition,
  stockCount: Math.max(0, Number(draft.stockCount) || 0),
  price: Math.max(0, Number(draft.price) || 0),
  priceType: draft.priceType,
  genreIds: draft.genreIds,
  tagIds: draft.tagIds,
  authorIds: draft.authorIds,
  newAuthorNames: draft.newAuthorNames
    .split(',')
    .map((name) => name.trim())
    .filter((name) => name.length > 0),
});

interface UserDraft {
  name: string;
  lastName: string;
  phoneNumber: string;
  nickname: string;
  role: UserRole;
  status: UserStatus;
}

const toUserDraft = (user: AdminUser): UserDraft => ({
  name: user.name ?? '',
  lastName: user.lastName ?? '',
  phoneNumber: user.phoneNumber ?? '',
  nickname: user.nickname ?? '',
  role: user.role,
  status: user.status,
});

const toUserRequest = (draft: UserDraft): AdminUserRequest => ({
  name: draft.name.trim(),
  lastName: draft.lastName.trim(),
  phoneNumber: draft.phoneNumber.trim(),
  nickname: draft.nickname.trim(),
  role: draft.role,
  status: draft.status,
});

function AdminPage() {
  const [searchParams] = useSearchParams();
  const requestedBookId = getAdminBookIdParam(searchParams);
  const [tab, setTab] = useState<AdminTab>(() => getAdminTabParam(searchParams));
  const [orders, setOrders] = useState<AdminOrder[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<OrderStatus | 'All'>('All');
  const [search, setSearch] = useState('');
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [statusDraft, setStatusDraft] = useState<OrderStatus>(OrderStatus.Created);
  const [commentDraft, setCommentDraft] = useState('');
  const [trackingDraft, setTrackingDraft] = useState('');
  const [itemsDraft, setItemsDraft] = useState<OrderItemDraft[]>([]);
  const [catalogItems, setCatalogItems] = useState<AdminCatalogItem[]>([]);
  const [newItemId, setNewItemId] = useState('');
  const [newItemSearch, setNewItemSearch] = useState('');
  const [savingOrder, setSavingOrder] = useState(false);

  const [isCreatingOrder, setIsCreatingOrder] = useState(false);
  const [newOrderUserSearch, setNewOrderUserSearch] = useState('');
  const [newOrderUserOptions, setNewOrderUserOptions] = useState<AdminUser[]>([]);
  const [newOrderUser, setNewOrderUser] = useState<AdminUser | null>(null);
  const [newOrderItems, setNewOrderItems] = useState<OrderItemDraft[]>([]);
  const [newOrderItemId, setNewOrderItemId] = useState('');
  const [newOrderItemSearch, setNewOrderItemSearch] = useState('');
  const [newOrderCatalogItems, setNewOrderCatalogItems] = useState<AdminCatalogItem[]>([]);
  const [newOrderKnownItems, setNewOrderKnownItems] = useState<AdminCatalogItem[]>([]);
  const [newOrderDelivery, setNewOrderDelivery] = useState<DeliveryInformationData>(emptyDelivery());
  const [newOrderUserComment, setNewOrderUserComment] = useState('');
  const [newOrderComment, setNewOrderComment] = useState('');
  const [creatingOrder, setCreatingOrder] = useState(false);
  const [createOrderMessage, setCreateOrderMessage] = useState<string | null>(null);
  const [isCreatingClient, setIsCreatingClient] = useState(false);
  const [newClientName, setNewClientName] = useState('');
  const [newClientLastName, setNewClientLastName] = useState('');
  const [newClientEmail, setNewClientEmail] = useState('');
  const [newClientPhone, setNewClientPhone] = useState('');
  const [creatingClient, setCreatingClient] = useState(false);

  const [bookId, setBookId] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [isPrimary, setIsPrimary] = useState(true);
  const [uploadedImage, setUploadedImage] = useState<ImageData | null>(null);
  const [imageError, setImageError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [books, setBooks] = useState<AdminBook[]>([]);
  const [publishers, setPublishers] = useState<Publisher[]>([]);
  const [genres, setGenres] = useState<Genre[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [authors, setAuthors] = useState<Author[]>([]);
  const [series, setSeries] = useState<Series[]>([]);
  const [bookSearch, setBookSearch] = useState('');
  const [selectedBookId, setSelectedBookId] = useState<number | null>(requestedBookId);
  const [bookDraft, setBookDraft] = useState<BookDraft>(emptyBookDraft);
  const [isBookCopyDraft, setIsBookCopyDraft] = useState(false);
  const [bookMessage, setBookMessage] = useState<string | null>(null);
  const [booksLoading, setBooksLoading] = useState(false);
  const [savingBook, setSavingBook] = useState(false);
  const [bookImageFile, setBookImageFile] = useState<File | null>(null);
  const [uploadingBookImage, setUploadingBookImage] = useState(false);

  const [importFile, setImportFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [importMessage, setImportMessage] = useState<string | null>(null);
  const [templateCopied, setTemplateCopied] = useState(false);

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [userSearch, setUserSearch] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [userDraft, setUserDraft] = useState<UserDraft | null>(null);
  const [usersLoading, setUsersLoading] = useState(false);
  const [savingUser, setSavingUser] = useState(false);
  const [userMessage, setUserMessage] = useState<string | null>(null);

  const sortedOrders = useMemo(
    () => [...orders].sort((a, b) => new Date(b.orderDate).getTime() - new Date(a.orderDate).getTime()),
    [orders]
  );

  const filteredOrders = useMemo(() => {
    const query = normalize(search);
    return sortedOrders.filter((order) => {
      const matchesStatus = selectedStatus === 'All' || order.orderStatus === selectedStatus;
      if (!matchesStatus) return false;
      if (!query) return true;

      return [
        order.id,
        order.userName,
        order.userLastName,
        order.userEmail,
        order.userPhoneNumber,
        order.trackingNumber,
        order.userDeliveryInformation?.city,
        ...order.items.map((item) => item.name),
      ].some((value) => normalize(value).includes(query));
    });
  }, [search, selectedStatus, sortedOrders]);

  const selectedOrder = useMemo(
    () => filteredOrders.find((order) => order.id === selectedOrderId) ?? filteredOrders[0] ?? null,
    [filteredOrders, selectedOrderId]
  );

  const statusCounts = useMemo(() => {
    const counts = new Map<OrderStatus, number>();
    orderStatuses.forEach((status) => counts.set(status, 0));
    orders.forEach((order) => counts.set(order.orderStatus, (counts.get(order.orderStatus) ?? 0) + 1));
    return counts;
  }, [orders]);

  const selectedBook = useMemo(
    () => books.find((book) => book.id === selectedBookId) ?? null,
    [books, selectedBookId]
  );

  const selectedUser = useMemo(
    () => users.find((user) => user.id === selectedUserId) ?? null,
    [users, selectedUserId]
  );

  const loadBooks = useCallback(async (searchValue = bookSearch, preferredBookId?: number | null) => {
    setBooksLoading(true);
    setBookMessage(null);
    try {
      const response = await getAdminBooks(searchValue, 0, preferredBookId ? 500 : 80);
      let items = response?.items ?? [];

      if (preferredBookId && !items.some((book) => book.id === preferredBookId)) {
        try {
          const preferredBook = await getAdminBook(preferredBookId);
          items = [preferredBook, ...items.filter((book) => book.id !== preferredBookId)];
        } catch (error) {
          void error;
        }
      }

      setBooks(items);
      setSelectedBookId((current) => {
        if (preferredBookId && items.some((book) => book.id === preferredBookId)) {
          return preferredBookId;
        }

        return current && items.some((book) => book.id === current)
          ? current
          : items[0]?.id ?? null;
      });
    } catch {
      setBookMessage('Не вдалося завантажити книги.');
    } finally {
      setBooksLoading(false);
    }
  }, [bookSearch]);

  const loadUsers = useCallback(async (searchValue = userSearch) => {
    setUsersLoading(true);
    setUserMessage(null);
    try {
      const response = await getAdminUsers(searchValue, 0, 100);
      const items = response.items ?? [];
      setUsers(items);
      setSelectedUserId((current) =>
        current && items.some((user) => user.id === current)
          ? current
          : items[0]?.id ?? null
      );
    } catch {
      setUserMessage('Не вдалося завантажити користувачів.');
    } finally {
      setUsersLoading(false);
    }
  }, [userSearch]);

  const loadOrders = useCallback(async () => {
    setOrdersLoading(true);
    setOrdersError(null);
    setSaveMessage(null);
    try {
      const response = await getOrders(undefined, 0, 500);
      setOrders(response.items ?? []);
      setSelectedOrderId((current) =>
        current && response.items.some((order) => order.id === current)
          ? current
          : response.items[0]?.id ?? null
      );
    } catch {
      setOrdersError('Не вдалося завантажити замовлення.');
    } finally {
      setOrdersLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  useEffect(() => {
    const nextTab = getAdminTabParam(searchParams);
    setTab(nextTab);

    const nextBookId = getAdminBookIdParam(searchParams);
    if (nextBookId) {
      setSelectedBookId(nextBookId);
    }
  }, [searchParams]);

  useEffect(() => {
    publishersServiceInstance.GetAll()
      .then(setPublishers)
      .catch(() => setPublishers([]));
    genresServiceInstance.GetAll()
      .then(setGenres)
      .catch(() => setGenres([]));
    tagsServiceInstance.GetAll()
      .then(setTags)
      .catch(() => setTags([]));
    authorsServiceInstance.GetAll()
      .then(setAuthors)
      .catch(() => setAuthors([]));
    seriesServiceInstance.GetAll()
      .then(setSeries)
      .catch(() => setSeries([]));
  }, []);

  useEffect(() => {
    if (tab !== 'books') return;

    const timeoutId = window.setTimeout(() => {
      loadBooks(bookSearch, requestedBookId);
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, [bookSearch, loadBooks, requestedBookId, tab]);

  useEffect(() => {
    if (tab !== 'users') return;

    const timeoutId = window.setTimeout(() => {
      loadUsers(userSearch);
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, [loadUsers, tab, userSearch]);

  useEffect(() => {
    const query = newItemSearch.trim();
    if (query.length < 4) {
      setCatalogItems([]);
      return;
    }

    const timeoutId = window.setTimeout(async () => {
      try {
        const response = await getCatalogItems(query, 0, 20);
        setCatalogItems((response ?? []).filter((item) => item.stockCount > 0));
      } catch {
        setCatalogItems([]);
      }
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, [newItemSearch]);

  useEffect(() => {
    if (!isCreatingOrder) return;

    const query = newOrderUserSearch.trim();
    if (query.length < 2) {
      setNewOrderUserOptions([]);
      return;
    }

    const timeoutId = window.setTimeout(async () => {
      try {
        const response = await getAdminUsers(query, 0, 20);
        setNewOrderUserOptions(response.items ?? []);
      } catch {
        setNewOrderUserOptions([]);
      }
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, [isCreatingOrder, newOrderUserSearch]);

  useEffect(() => {
    const query = newOrderItemSearch.trim();
    if (query.length < 4) {
      setNewOrderCatalogItems([]);
      return;
    }

    const timeoutId = window.setTimeout(async () => {
      try {
        const response = await getCatalogItems(query, 0, 20);
        setNewOrderCatalogItems((response ?? []).filter((item) => item.stockCount > 0));
      } catch {
        setNewOrderCatalogItems([]);
      }
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, [newOrderItemSearch]);

  useEffect(() => {
    if (!selectedOrder) return;

    setStatusDraft(selectedOrder.orderStatus);
    setCommentDraft(selectedOrder.comment ?? '');
    setTrackingDraft(selectedOrder.trackingNumber ?? '');
    setItemsDraft(selectedOrder.items.map((item) => ({
      itemId: String(item.itemId),
      count: String(item.count),
    })));
    setNewItemId('');
    setNewItemSearch('');
  }, [selectedOrder]);

  useEffect(() => {
    if (!selectedBook) {
      if (!isBookCopyDraft) {
        setBookDraft(emptyBookDraft);
      }
      return;
    }

    setIsBookCopyDraft(false);
    setBookDraft(toBookDraft(selectedBook));
    setBookImageFile(null);
  }, [isBookCopyDraft, selectedBook]);

  useEffect(() => {
    setUserDraft(selectedUser ? toUserDraft(selectedUser) : null);
  }, [selectedUser]);

  const handleStatusSave = async () => {
    if (!selectedOrder) return;
    setSavingOrder(true);
    setSaveMessage(null);
    try {
      const updated = await updateOrderStatus(selectedOrder.id, {
        status: statusDraft,
        comment: commentDraft,
        trackingNumber: trackingDraft,
      });
      setOrders((current) => current.map((order) => order.id === updated.id ? updated : order));
      setSelectedOrderId(updated.id);
      setSaveMessage('Зміни по замовленню збережено.');
    } catch {
      setSaveMessage('Не вдалося зберегти зміни по замовленню.');
    } finally {
      setSavingOrder(false);
    }
  };

  const handleItemsSave = async () => {
    if (!selectedOrder) return;
    const items = itemsDraft
      .map((item) => ({
        itemId: Number(item.itemId),
        count: Number(item.count),
      }))
      .filter((item) => item.itemId > 0 && item.count > 0);

    setSavingOrder(true);
    setSaveMessage(null);
    try {
      const updated = await updateOrderItems(selectedOrder.id, {
        items,
        comment: commentDraft,
      });
      setOrders((current) => current.map((order) => order.id === updated.id ? updated : order));
      setSelectedOrderId(updated.id);
      setSaveMessage('Склад замовлення оновлено.');
    } catch {
      setSaveMessage('Не вдалося оновити склад замовлення.');
    } finally {
      setSavingOrder(false);
    }
  };

  const resetNewOrderForm = () => {
    setNewOrderUserSearch('');
    setNewOrderUserOptions([]);
    setNewOrderUser(null);
    setNewOrderItems([]);
    setNewOrderItemId('');
    setNewOrderItemSearch('');
    setNewOrderCatalogItems([]);
    setNewOrderKnownItems([]);
    setNewOrderDelivery(emptyDelivery());
    setNewOrderUserComment('');
    setNewOrderComment('');
    setCreateOrderMessage(null);
    setIsCreatingClient(false);
    setNewClientName('');
    setNewClientLastName('');
    setNewClientEmail('');
    setNewClientPhone('');
  };

  const handleOpenCreateOrder = () => {
    resetNewOrderForm();
    setIsCreatingOrder(true);
  };

  const handleSelectNewOrderUser = (user: AdminUser | null) => {
    setNewOrderUser(user);
    setNewOrderDelivery((current) => ({
      ...current,
      name: user?.name ?? '',
      lastName: user?.lastName ?? '',
      phone: user?.phoneNumber ?? '',
    }));
  };

  const handleOpenCreateClient = (initialQuery: string) => {
    const [firstGuess, ...restGuess] = initialQuery.trim().split(/\s+/).filter(Boolean);
    setNewClientName(firstGuess ?? '');
    setNewClientLastName(restGuess.join(' '));
    setNewClientEmail('');
    setNewClientPhone('');
    setIsCreatingClient(true);
  };

  const handleCreateClientSubmit = async () => {
    if (!newClientName.trim() || !newClientLastName.trim()) {
      setCreateOrderMessage("Вкажіть ім'я та прізвище клієнта.");
      return;
    }

    setCreatingClient(true);
    setCreateOrderMessage(null);
    try {
      const created = await createAdminUser({
        name: newClientName.trim(),
        lastName: newClientLastName.trim(),
        email: newClientEmail.trim() || undefined,
        phoneNumber: newClientPhone.trim() || undefined,
      });
      handleSelectNewOrderUser(created);
      setNewOrderUserSearch(`${created.name} ${created.lastName}`.trim());
      setIsCreatingClient(false);
    } catch {
      setCreateOrderMessage('Не вдалося створити клієнта. Можливо, такий email вже використовується.');
    } finally {
      setCreatingClient(false);
    }
  };

  const handleCreateOrderSubmit = async () => {
    if (!newOrderUser) {
      setCreateOrderMessage('Виберіть клієнта.');
      return;
    }

    const items = newOrderItems
      .map((item) => ({ itemId: Number(item.itemId), count: Number(item.count) }))
      .filter((item) => item.itemId > 0 && item.count > 0);

    if (!items.length) {
      setCreateOrderMessage('Додайте хоча б одну книгу.');
      return;
    }

    if (
      !newOrderDelivery.name.trim() ||
      !newOrderDelivery.lastName.trim() ||
      !newOrderDelivery.phone.trim() ||
      (newOrderDelivery.provider !== DeliveryProvider.Self &&
        (!newOrderDelivery.city.trim() || !newOrderDelivery.number.trim()))
    ) {
      setCreateOrderMessage('Заповніть отримувача та адресу доставки повністю.');
      return;
    }

    setCreatingOrder(true);
    setCreateOrderMessage(null);
    try {
      const created = await createAdminOrder({
        userId: newOrderUser.id,
        items,
        userComment: newOrderUserComment.trim() || undefined,
        comment: newOrderComment.trim() || undefined,
        deliveryAddress: toDeliveryPayload(newOrderDelivery),
      });
      setOrders((current) => [created, ...current]);
      setSelectedOrderId(created.id);
      setIsCreatingOrder(false);
      resetNewOrderForm();
      setSaveMessage('Замовлення для клієнта створено.');
    } catch {
      setCreateOrderMessage('Не вдалося створити замовлення.');
    } finally {
      setCreatingOrder(false);
    }
  };

  const handleImageSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const parsedBookId = Number(bookId);

    if (!parsedBookId || parsedBookId <= 0 || !file) {
      setImageError('Вкажіть id книги та виберіть файл зображення.');
      return;
    }

    try {
      setIsUploading(true);
      setImageError(null);
      const image = await uploadBookImage(parsedBookId, file, isPrimary);
      setUploadedImage(image);
    } catch {
      setImageError('Не вдалося завантажити зображення.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleNewBook = () => {
    setIsBookCopyDraft(false);
    setSelectedBookId(null);
    setBookDraft(emptyBookDraft);
    setBookImageFile(null);
    setBookMessage(null);
  };

  const handlePublisherCreate = async (name: string) => {
    const created = await publishersServiceInstance.Create(name.trim());
    setPublishers((current) => [...current, created].sort((a, b) => a.name.localeCompare(b.name, 'uk')));
    setBookDraft((current) => ({ ...current, publisherId: String(created.id) }));
  };

  const handleBookCopy = () => {
    if (!selectedBook) return;

    setIsBookCopyDraft(true);
    setSelectedBookId(null);
    setBookDraft(toBookDraft(selectedBook));
    setBookImageFile(null);
    setBookMessage('Копію створено. Відредагуйте її та збережіть як нову книгу.');
  };

  const handleBookSave = async () => {
    if (!bookDraft.name.trim()) {
      setBookMessage('Вкажіть назву книги.');
      return;
    }

    if (!bookDraft.publisherId) {
      setBookMessage('Виберіть видавництво.');
      return;
    }

    setSavingBook(true);
    setBookMessage(null);
    try {
      const request = toBookRequest(bookDraft);
      let savedBookId = selectedBook?.id ?? null;
      if (selectedBook) {
        await updateAdminBook(selectedBook.id, request);
        setBookMessage('Книгу оновлено.');
      } else {
        const created = await createAdminBook(request);
        savedBookId = created.id;
        setIsBookCopyDraft(false);
        setSelectedBookId(created.id);
        setBookMessage('Книгу створено.');
      }
      if (request.newSeriesName) {
        seriesServiceInstance.GetAll().then(setSeries).catch(() => undefined);
      }
      await loadBooks(bookSearch, savedBookId);
    } catch {
      setBookMessage('Не вдалося зберегти книгу.');
    } finally {
      setSavingBook(false);
    }
  };

  const handleBookDelete = async () => {
    if (!selectedBook) return;

    setSavingBook(true);
    setBookMessage(null);
    try {
      await deleteAdminBook(selectedBook.id);
      setBookMessage('Книгу прибрано з каталогу.');
      setIsBookCopyDraft(false);
      setSelectedBookId(null);
      await loadBooks(bookSearch);
    } catch {
      setBookMessage('Не вдалося видалити книгу.');
    } finally {
      setSavingBook(false);
    }
  };

  const handleBookPermanentDelete = async () => {
    if (!selectedBook) return;

    const confirmed = window.confirm(
      `Видалити книгу "${selectedBook.name}" назавжди? Це прибере її з бази, замовлень, кошиків, списків бажань, підбірок і видалить файли її фото.`
    );
    if (!confirmed) return;

    setSavingBook(true);
    setBookMessage(null);
    try {
      await deleteAdminBookPermanently(selectedBook.id);
      setBookMessage('Книгу повністю видалено з каталогу.');
      setIsBookCopyDraft(false);
      setSelectedBookId(null);
      await loadBooks(bookSearch);
    } catch {
      setBookMessage('Не вдалося повністю видалити книгу.');
    } finally {
      setSavingBook(false);
    }
  };

  const handleUserSave = async () => {
    if (!selectedUser || !userDraft) return;

    if (!userDraft.name.trim() || !userDraft.lastName.trim()) {
      setUserMessage("Вкажіть ім'я та прізвище.");
      return;
    }

    setSavingUser(true);
    setUserMessage(null);
    try {
      const updated = await updateAdminUser(selectedUser.id, toUserRequest(userDraft));
      setUsers((current) => current.map((user) => (user.id === updated.id ? updated : user)));
      setUserMessage('Користувача оновлено.');
    } catch {
      setUserMessage('Не вдалося зберегти зміни користувача.');
    } finally {
      setSavingUser(false);
    }
  };

  const handleBookImageUpload = async () => {
    if (!selectedBook || !bookImageFile) {
      setBookMessage('Виберіть книгу та файл зображення.');
      return;
    }

    setUploadingBookImage(true);
    setBookMessage(null);
    try {
      await uploadBookImage(selectedBook.id, bookImageFile, true);
      setBookImageFile(null);
      setBookMessage('Фото завантажено.');
      await loadBooks(bookSearch, selectedBook.id);
    } catch {
      setBookMessage('Не вдалося завантажити фото.');
    } finally {
      setUploadingBookImage(false);
    }
  };

  const handleImportBooks = async () => {
    if (!importFile) {
      setImportMessage('Виберіть CSV-файл.');
      return;
    }

    setImporting(true);
    setImportMessage(null);
    try {
      const result = await importBooksFromCsv(importFile);
      setImportFile(null);
      setImportMessage(`Імпортовано книг: ${result.importedCount}.`);
      await loadBooks();
    } catch {
      setImportMessage('Не вдалося імпортувати файл.');
    } finally {
      setImporting(false);
    }
  };

  const handleCopyImportTemplate = async () => {
    try {
      await navigator.clipboard.writeText(csvImportTemplate);
      setTemplateCopied(true);
      setTimeout(() => setTemplateCopied(false), 2000);
    } catch {
      setTemplateCopied(false);
    }
  };

  return (
    <div className="admin-page default-container">
      <header className="admin-topbar">
        <div>
          <p className="admin-kicker">Readerly CRM</p>
          <h1>Замовлення та оренди</h1>
        </div>
        <Button variant="outlined" onClick={loadOrders} disabled={ordersLoading}>
          Оновити
        </Button>
      </header>

      <Tabs
        value={tab}
        onChange={(_, value) => setTab(value)}
        className="admin-tabs"
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
      >
        <Tab value="orders" label="Замовлення" />
        <Tab value="books" label="Книги" />
        <Tab value="images" label="Зображення книг" />
        <Tab value="users" label="Користувачі" />
        <Tab value="import" label="Імпорт" />
        <Tab value="collections" label="Підбірки" />
        <Tab value="waitlist" label="Черга" />
        <Tab value="shop" label="Крамничка" />
      </Tabs>

      {tab === 'orders' && (
        <section className="admin-orders">
          <div className="admin-stats">
            <button
              type="button"
              className={`admin-stat ${selectedStatus === 'All' ? 'active' : ''}`}
              onClick={() => setSelectedStatus('All')}
            >
              <span>Всі</span>
              <strong>{orders.length}</strong>
            </button>
            {orderStatuses.map((status) => (
              <button
                type="button"
                key={status}
                className={`admin-stat ${selectedStatus === status ? 'active' : ''}`}
                onClick={() => setSelectedStatus(status)}
              >
                <span>{statusLabels[status]}</span>
                <strong>{statusCounts.get(status) ?? 0}</strong>
              </button>
            ))}
          </div>

          <div className="admin-toolbar">
            <TextField
              select
              label="Статус"
              value={selectedStatus}
              onChange={(event) => setSelectedStatus(event.target.value as OrderStatus | 'All')}
              size="small"
            >
              <MenuItem value="All">Всі статуси</MenuItem>
              {orderStatuses.map((status) => (
                <MenuItem key={status} value={status}>{statusLabels[status]}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Пошук"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              size="small"
              placeholder="ID, клієнт, телефон, книга, ТТН"
              className="admin-search"
            />
            <Button variant="contained" onClick={handleOpenCreateOrder}>
              Нове замовлення
            </Button>
          </div>

          {ordersError && <Alert severity="error">{ordersError}</Alert>}
          {saveMessage && <Alert severity={isErrorMessage(saveMessage) ? 'error' : 'success'}>{saveMessage}</Alert>}

          <div className="admin-orders-layout">
            <aside className="admin-order-list">
              <div className="admin-list-heading">
                <strong>{filteredOrders.length} замовлень</strong>
                <span>нові зверху</span>
              </div>
              {ordersLoading ? (
                <CircularProgress />
              ) : filteredOrders.length ? (
                filteredOrders.map((order) => (
                  <button
                    type="button"
                    key={order.id}
                    className={`admin-order-row ${!isCreatingOrder && selectedOrder?.id === order.id ? 'selected' : ''}`}
                    onClick={() => {
                      setIsCreatingOrder(false);
                      setSelectedOrderId(order.id);
                    }}
                  >
                    <div className="admin-order-row-main">
                      <strong>#{order.id}</strong>
                      <span>{statusLabels[order.orderStatus]}</span>
                    </div>
                    <span>{order.userName} {order.userLastName}</span>
                    <small>{formatDate(order.orderDate)} · {money(order.totalPrice)}</small>
                  </button>
                ))
              ) : (
                <p className="admin-empty">Немає замовлень за цим фільтром.</p>
              )}
            </aside>

            {isCreatingOrder ? (
              <CreateOrderForm
                userSearch={newOrderUserSearch}
                userOptions={newOrderUserOptions}
                selectedUser={newOrderUser}
                items={newOrderItems}
                catalogItems={newOrderCatalogItems}
                knownCatalogItems={newOrderKnownItems}
                newItemId={newOrderItemId}
                newItemSearch={newOrderItemSearch}
                delivery={newOrderDelivery}
                userComment={newOrderUserComment}
                comment={newOrderComment}
                saving={creatingOrder}
                message={createOrderMessage}
                isCreatingClient={isCreatingClient}
                newClientName={newClientName}
                newClientLastName={newClientLastName}
                newClientEmail={newClientEmail}
                newClientPhone={newClientPhone}
                creatingClient={creatingClient}
                onUserSearchChange={setNewOrderUserSearch}
                onSelectUser={handleSelectNewOrderUser}
                onItemsChange={setNewOrderItems}
                onNewItemIdChange={setNewOrderItemId}
                onNewItemSearchChange={setNewOrderItemSearch}
                onKnownCatalogItemsChange={setNewOrderKnownItems}
                onDeliveryChange={setNewOrderDelivery}
                onUserCommentChange={setNewOrderUserComment}
                onCommentChange={setNewOrderComment}
                onOpenCreateClient={handleOpenCreateClient}
                onCancelCreateClient={() => setIsCreatingClient(false)}
                onNewClientNameChange={setNewClientName}
                onNewClientLastNameChange={setNewClientLastName}
                onNewClientEmailChange={setNewClientEmail}
                onNewClientPhoneChange={setNewClientPhone}
                onCreateClientSubmit={handleCreateClientSubmit}
                onCancel={() => setIsCreatingOrder(false)}
                onSubmit={handleCreateOrderSubmit}
              />
            ) : selectedOrder ? (
              <OrderDetails
                order={selectedOrder}
                statusDraft={statusDraft}
                commentDraft={commentDraft}
                trackingDraft={trackingDraft}
                itemsDraft={itemsDraft}
                catalogItems={catalogItems}
                newItemId={newItemId}
                newItemSearch={newItemSearch}
                savingOrder={savingOrder}
                onStatusChange={setStatusDraft}
                onCommentChange={setCommentDraft}
                onTrackingChange={setTrackingDraft}
                onItemsChange={setItemsDraft}
                onNewItemIdChange={setNewItemId}
                onNewItemSearchChange={setNewItemSearch}
                onStatusSave={handleStatusSave}
                onItemsSave={handleItemsSave}
              />
            ) : (
              <div className="admin-order-detail admin-empty-detail">Виберіть замовлення зі списку.</div>
            )}
          </div>
        </section>
      )}

      {tab === 'books' && (
        <AdminBooksPanel
          books={books}
          publishers={publishers}
          genres={genres}
          tags={tags}
          authors={authors}
          series={series}
          selectedBook={selectedBook}
          selectedBookId={selectedBookId}
          bookSearch={bookSearch}
          bookDraft={bookDraft}
          bookMessage={bookMessage}
          booksLoading={booksLoading}
          savingBook={savingBook}
          bookImageFile={bookImageFile}
          uploadingBookImage={uploadingBookImage}
          onBookSearchChange={setBookSearch}
          onSelectBook={setSelectedBookId}
          onDraftChange={setBookDraft}
          onNewBook={handleNewBook}
          onCreatePublisher={handlePublisherCreate}
          onCopyBook={handleBookCopy}
          onSaveBook={handleBookSave}
          onDeleteBook={handleBookDelete}
          onPermanentDeleteBook={handleBookPermanentDelete}
          onBookImageFileChange={setBookImageFile}
          onUploadBookImage={handleBookImageUpload}
        />
      )}

      {tab === 'images' && (
        <form className="admin-image-form" onSubmit={handleImageSubmit}>
          <TextField
            label="ID книги"
            value={bookId}
            onChange={(event) => setBookId(event.target.value)}
            inputProps={{ inputMode: 'numeric' }}
          />
          <Button variant="outlined" component="label">
            {file ? file.name : 'Вибрати зображення'}
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              hidden
              onChange={(event) => setFile(event.target.files?.[0] ?? null)}
            />
          </Button>
          <FormControlLabel
            control={<Checkbox checked={isPrimary} onChange={(event) => setIsPrimary(event.target.checked)} />}
            label="Основне зображення"
          />
          <Button type="submit" variant="contained" disabled={isUploading}>
            {isUploading ? 'Завантажуємо...' : 'Завантажити'}
          </Button>
          {imageError && <Alert severity="error">{imageError}</Alert>}
          {uploadedImage && <Alert severity="success">Зображення #{uploadedImage.id} завантажено.</Alert>}
        </form>
      )}

      {tab === 'users' && (
        <AdminUsersPanel
          users={users}
          selectedUser={selectedUser}
          selectedUserId={selectedUserId}
          userSearch={userSearch}
          userDraft={userDraft}
          userMessage={userMessage}
          usersLoading={usersLoading}
          savingUser={savingUser}
          onUserSearchChange={setUserSearch}
          onSelectUser={setSelectedUserId}
          onDraftChange={setUserDraft}
          onSaveUser={handleUserSave}
        />
      )}

      {tab === 'import' && (
        <div className="admin-import-form">
          <h2>Імпорт книг з CSV</h2>
          <p>
            Файл — таблиця CSV з роздільником "," і рядком заголовків. Один рядок — одна книга,
            одна книга — один автор і один тег. Приклад шаблону нижче можна скопіювати
            (першим рядком заголовки, другим — приклад заповнення) й відредагувати у будь-якому
            табличному редакторі перед завантаженням.
          </p>
          <pre className="admin-import-template">{csvImportTemplate}</pre>
          <Button variant="outlined" onClick={handleCopyImportTemplate}>
            {templateCopied ? 'Скопійовано!' : 'Скопіювати шаблон'}
          </Button>

          <Button variant="outlined" component="label">
            {importFile ? importFile.name : 'Вибрати CSV-файл'}
            <input
              type="file"
              accept=".csv,text/csv"
              hidden
              onChange={(event) => setImportFile(event.target.files?.[0] ?? null)}
            />
          </Button>
          <Button variant="contained" onClick={handleImportBooks} disabled={importing}>
            {importing ? 'Імпортуємо...' : 'Імпортувати'}
          </Button>
          {importMessage && <Alert severity={isErrorMessage(importMessage) ? 'error' : 'success'}>{importMessage}</Alert>}
        </div>
      )}

      {tab === 'collections' && <AdminCollectionsPanel />}

      {tab === 'waitlist' && <AdminWaitlistPanel />}

      {tab === 'shop' && (
        <>
          <AdminShopInquiries />
          <AdminShopPanel />
        </>
      )}
    </div>
  );
}

interface OrderDetailsProps {
  order: AdminOrder;
  statusDraft: OrderStatus;
  commentDraft: string;
  trackingDraft: string;
  itemsDraft: OrderItemDraft[];
  catalogItems: AdminCatalogItem[];
  newItemId: string;
  newItemSearch: string;
  savingOrder: boolean;
  onStatusChange: (value: OrderStatus) => void;
  onCommentChange: (value: string) => void;
  onTrackingChange: (value: string) => void;
  onItemsChange: (value: OrderItemDraft[]) => void;
  onNewItemIdChange: (value: string) => void;
  onNewItemSearchChange: (value: string) => void;
  onStatusSave: () => void;
  onItemsSave: () => void;
}

interface AdminBooksPanelProps {
  books: AdminBook[];
  publishers: Publisher[];
  genres: Genre[];
  tags: Tag[];
  authors: Author[];
  series: Series[];
  selectedBook: AdminBook | null;
  selectedBookId: number | null;
  bookSearch: string;
  bookDraft: BookDraft;
  bookMessage: string | null;
  booksLoading: boolean;
  savingBook: boolean;
  bookImageFile: File | null;
  uploadingBookImage: boolean;
  onBookSearchChange: (value: string) => void;
  onSelectBook: (value: number | null) => void;
  onDraftChange: (value: BookDraft) => void;
  onNewBook: () => void;
  onCreatePublisher: (name: string) => Promise<void>;
  onCopyBook: () => void;
  onSaveBook: () => void;
  onDeleteBook: () => void;
  onPermanentDeleteBook: () => void;
  onBookImageFileChange: (value: File | null) => void;
  onUploadBookImage: () => void;
}

function AdminBooksPanel({
  books,
  publishers,
  genres,
  tags,
  authors,
  series,
  selectedBook,
  selectedBookId,
  bookSearch,
  bookDraft,
  bookMessage,
  booksLoading,
  savingBook,
  bookImageFile,
  uploadingBookImage,
  onBookSearchChange,
  onSelectBook,
  onDraftChange,
  onNewBook,
  onCreatePublisher,
  onCopyBook,
  onSaveBook,
  onDeleteBook,
  onPermanentDeleteBook,
  onBookImageFileChange,
  onUploadBookImage,
}: AdminBooksPanelProps) {
  const updateDraft = (patch: Partial<BookDraft>) => onDraftChange({ ...bookDraft, ...patch });
  const [publisherDialogOpen, setPublisherDialogOpen] = useState(false);
  const [publisherName, setPublisherName] = useState('');
  const [publisherError, setPublisherError] = useState<string | null>(null);
  const [creatingPublisher, setCreatingPublisher] = useState(false);

  const openPublisherDialog = () => {
    setPublisherName('');
    setPublisherError(null);
    setPublisherDialogOpen(true);
  };

  const closePublisherDialog = () => {
    if (!creatingPublisher) setPublisherDialogOpen(false);
  };

  const submitPublisher = async (event: FormEvent) => {
    event.preventDefault();
    const name = publisherName.trim();
    if (!name) {
      setPublisherError('Вкажіть назву видавництва.');
      return;
    }

    setCreatingPublisher(true);
    setPublisherError(null);
    try {
      await onCreatePublisher(name);
      setPublisherDialogOpen(false);
    } catch (error) {
      setPublisherError(error instanceof ApiError && error.status === 409
        ? 'Видавництво з такою назвою вже існує.'
        : 'Не вдалося створити видавництво.');
    } finally {
      setCreatingPublisher(false);
    }
  };

  return (
    <section className="admin-books-layout">
      <aside className="admin-book-list">
        <div className="admin-book-toolbar">
          <TextField
            label="Пошук книги"
            value={bookSearch}
            onChange={(event) => onBookSearchChange(event.target.value)}
            size="small"
            placeholder="Назва, серія, ISBN, видавництво"
          />
          <Button variant="contained" onClick={onNewBook}>Нова книга</Button>
        </div>

        {booksLoading ? (
          <CircularProgress />
        ) : books.length ? (
          <div className="admin-book-rows">
            {books.map((book) => (
              <button
                type="button"
                key={book.id}
                className={`admin-book-row ${selectedBookId === book.id ? 'selected' : ''}`}
                onClick={() => onSelectBook(book.id)}
              >
                <img
                  src={getBookImageUrl({
                    id: book.id,
                    image: undefined,
                    primaryImage: book.primaryImage,
                    images: book.images,
                    isbn: book.isbn,
                  })}
                  alt=""
                />
                <span>
                  <strong>{book.name}</strong>
                  <small>{book.publisherName || 'Без видавництва'} · {book.stockCount} шт. · {money(book.price)}</small>
                </span>
              </button>
            ))}
          </div>
        ) : (
          <p className="admin-empty">Книг не знайдено.</p>
        )}
      </aside>

      <article className="admin-book-editor">
        <header className="admin-order-header">
          <div>
            <p className="admin-kicker">{selectedBook ? `Книга #${selectedBook.id}` : 'Нова книга'}</p>
            <h2>{selectedBook ? selectedBook.name : 'Створити книгу'}</h2>
          </div>
          {selectedBook && <span className={selectedBook.stockCount > 0 ? 'admin-status status-Completed' : 'admin-status status-Cancelled'}>{selectedBook.stockCount > 0 ? 'Є в наявності' : 'Немає'}</span>}
        </header>

        {bookMessage && <Alert severity={isErrorMessage(bookMessage) ? 'error' : 'success'}>{bookMessage}</Alert>}

        <div className="admin-book-form">
          <TextField label="Назва" value={bookDraft.name} onChange={(event) => updateDraft({ name: event.target.value })} />
          <div className="admin-publisher-field">
            <TextField
              select
              fullWidth
              label="Видавництво"
              value={bookDraft.publisherId}
              onChange={(event) => updateDraft({ publisherId: event.target.value })}
            >
              <MenuItem value="">Виберіть видавництво</MenuItem>
              {publishers.map((publisher) => (
                <MenuItem key={publisher.id} value={String(publisher.id)}>{publisher.name}</MenuItem>
              ))}
            </TextField>
            <Tooltip title="Створити видавництво">
              <IconButton aria-label="Створити видавництво" onClick={openPublisherDialog}>
                <AddIcon />
              </IconButton>
            </Tooltip>
          </div>
          <Autocomplete
            options={series}
            value={series.find((item) => String(item.id) === bookDraft.seriesId) ?? null}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(_, value) => updateDraft({
              seriesId: value ? String(value.id) : '',
              newSeriesName: value ? '' : bookDraft.newSeriesName,
            })}
            noOptionsText="Серій не знайдено"
            renderInput={(params) => <TextField {...params} label="Серія" />}
          />
          <TextField
            label="Або нова серія"
            value={bookDraft.newSeriesName}
            onChange={(event) => updateDraft({
              newSeriesName: event.target.value,
              seriesId: event.target.value ? '' : bookDraft.seriesId,
            })}
            disabled={!!bookDraft.seriesId}
            helperText={bookDraft.seriesId ? undefined : 'Якщо потрібної серії немає у списку — впишіть назву тут'}
          />
          <TextField
            label="Номер у серії"
            type="number"
            value={bookDraft.seriesNumber}
            onChange={(event) => updateDraft({ seriesNumber: event.target.value })}
            disabled={!bookDraft.seriesId && !bookDraft.newSeriesName.trim()}
          />
          <TextField
            select
            label="Тип ціни"
            value={bookDraft.priceType}
            onChange={(event) => updateDraft({ priceType: event.target.value as BookPriceType })}
          >
            <MenuItem value={BookPriceType.Rental}>Оренда</MenuItem>
            <MenuItem value={BookPriceType.Sale}>Продаж</MenuItem>
          </TextField>
          <TextField label="Ціна" type="number" value={bookDraft.price} onChange={(event) => updateDraft({ price: event.target.value })} />
          <TextField label="Примірників" type="number" value={bookDraft.stockCount} onChange={(event) => updateDraft({ stockCount: event.target.value })} />
          <TextField label="ISBN" value={bookDraft.isbn} onChange={(event) => updateDraft({ isbn: event.target.value })} />
          <TextField label="Сторінок" type="number" value={bookDraft.pages} onChange={(event) => updateDraft({ pages: event.target.value })} />
          <TextField label="Рейтинг" type="number" value={bookDraft.rating} onChange={(event) => updateDraft({ rating: event.target.value })} />
          <TextField label="Рейтинг Readerly" type="number" value={bookDraft.internalRating} onChange={(event) => updateDraft({ internalRating: event.target.value })} />
          <Autocomplete
            multiple
            className="admin-book-field-wide"
            options={authors}
            value={authors.filter((author) => bookDraft.authorIds.includes(author.id))}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(_, value) => updateDraft({ authorIds: value.map((author) => author.id) })}
            renderInput={(params) => <TextField {...params} label="Автори" />}
          />
          <TextField
            className="admin-book-field-wide"
            label="Або нові автори (через кому)"
            value={bookDraft.newAuthorNames}
            onChange={(event) => updateDraft({ newAuthorNames: event.target.value })}
            helperText="Якщо потрібного автора немає у списку — впишіть ім'я тут; кілька авторів розділяйте комою"
          />
          <Autocomplete
            multiple
            className="admin-book-field-wide"
            options={genres}
            value={genres.filter((genre) => bookDraft.genreIds.includes(genre.id))}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(_, value) => updateDraft({ genreIds: value.map((genre) => genre.id) })}
            renderInput={(params) => <TextField {...params} label="Жанри" />}
          />
          <Autocomplete
            multiple
            className="admin-book-field-wide"
            options={tags}
            value={tags.filter((tag) => bookDraft.tagIds.includes(tag.id))}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(_, value) => updateDraft({ tagIds: value.map((tag) => tag.id) })}
            renderInput={(params) => <TextField {...params} label="Теги" />}
          />
          <TextField
            className="admin-book-field-wide"
            label="Опис"
            value={bookDraft.description}
            onChange={(event) => updateDraft({ description: event.target.value })}
            multiline
            minRows={4}
          />
          <FormControlLabel
            control={<Checkbox checked={bookDraft.isLimitedEdition} onChange={(event) => updateDraft({ isLimitedEdition: event.target.checked })} />}
            label="Лімітоване видання"
          />
        </div>

        <Dialog open={publisherDialogOpen} onClose={closePublisherDialog} fullWidth maxWidth="xs">
          <form onSubmit={submitPublisher}>
            <DialogTitle>Нове видавництво</DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                fullWidth
                label="Назва"
                value={publisherName}
                onChange={(event) => setPublisherName(event.target.value)}
                error={!!publisherError}
                helperText={publisherError}
                disabled={creatingPublisher}
                margin="dense"
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={closePublisherDialog} disabled={creatingPublisher}>Скасувати</Button>
              <Button type="submit" variant="contained" disabled={creatingPublisher || !publisherName.trim()}>
                {creatingPublisher ? 'Створюємо...' : 'Створити'}
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        <div className="admin-actions">
          <Button variant="contained" onClick={onSaveBook} disabled={savingBook}>
            {savingBook ? 'Зберігаємо...' : selectedBook ? 'Зберегти книгу' : 'Створити книгу'}
          </Button>
          {selectedBook && (
            <Button variant="outlined" startIcon={<ContentCopyIcon />} onClick={onCopyBook} disabled={savingBook}>
              Зробити копію
            </Button>
          )}
          {selectedBook && (
            <Button color="error" variant="outlined" onClick={onDeleteBook} disabled={savingBook}>
              Прибрати з каталогу
            </Button>
          )}
          {selectedBook && (
            <Button color="error" variant="contained" onClick={onPermanentDeleteBook} disabled={savingBook}>
              Видалити назавжди
            </Button>
          )}
        </div>

        {selectedBook && (
          <section className="admin-book-images">
            <h3>Фото книги</h3>
            <div className="admin-book-image-preview">
              <img
                src={getBookImageUrl({
                  id: selectedBook.id,
                  image: undefined,
                  primaryImage: selectedBook.primaryImage,
                  images: selectedBook.images,
                  isbn: selectedBook.isbn,
                })}
                alt=""
              />
              <div>
                <Button variant="outlined" component="label">
                  {bookImageFile ? bookImageFile.name : 'Вибрати фото'}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    hidden
                    onChange={(event) => onBookImageFileChange(event.target.files?.[0] ?? null)}
                  />
                </Button>
                <Button variant="contained" onClick={onUploadBookImage} disabled={!bookImageFile || uploadingBookImage}>
                  {uploadingBookImage ? 'Завантажуємо...' : 'Завантажити як основне'}
                </Button>
              </div>
            </div>
          </section>
        )}
      </article>
    </section>
  );
}

interface AdminUsersPanelProps {
  users: AdminUser[];
  selectedUser: AdminUser | null;
  selectedUserId: number | null;
  userSearch: string;
  userDraft: UserDraft | null;
  userMessage: string | null;
  usersLoading: boolean;
  savingUser: boolean;
  onUserSearchChange: (value: string) => void;
  onSelectUser: (value: number | null) => void;
  onDraftChange: (value: UserDraft | null) => void;
  onSaveUser: () => void;
}

function AdminUsersPanel({
  users,
  selectedUser,
  selectedUserId,
  userSearch,
  userDraft,
  userMessage,
  usersLoading,
  savingUser,
  onUserSearchChange,
  onSelectUser,
  onDraftChange,
  onSaveUser,
}: AdminUsersPanelProps) {
  const updateDraft = (patch: Partial<UserDraft>) => {
    if (!userDraft) return;
    onDraftChange({ ...userDraft, ...patch });
  };

  return (
    <section className="admin-books-layout">
      <aside className="admin-book-list">
        <div className="admin-book-toolbar">
          <TextField
            label="Пошук користувача"
            value={userSearch}
            onChange={(event) => onUserSearchChange(event.target.value)}
            size="small"
            placeholder="Ім'я, email, телефон"
          />
        </div>

        {usersLoading ? (
          <CircularProgress />
        ) : users.length ? (
          <div className="admin-book-rows">
            {users.map((user) => (
              <button
                type="button"
                key={user.id}
                className={`admin-order-row ${selectedUserId === user.id ? 'selected' : ''}`}
                onClick={() => onSelectUser(user.id)}
              >
                <div className="admin-order-row-main">
                  <strong>{user.name} {user.lastName}</strong>
                  <span>{user.hasLogin ? roleLabels[user.role] : 'Без входу'}</span>
                </div>
                <small>{user.email}</small>
              </button>
            ))}
          </div>
        ) : (
          <p className="admin-empty">Користувачів не знайдено.</p>
        )}
      </aside>

      <article className="admin-book-editor">
        {selectedUser && userDraft ? (
          <>
            <header className="admin-order-header">
              <div>
                <p className="admin-kicker">Користувач #{selectedUser.id}</p>
                <h2>{selectedUser.name} {selectedUser.lastName}</h2>
                <p>{selectedUser.email}</p>
              </div>
              <span className={`admin-status ${selectedUser.status === UserStatus.Active ? 'status-Completed' : 'status-Cancelled'}`}>
                {statusUserLabels[selectedUser.status]}
              </span>
            </header>

            {userMessage && (
              <Alert severity={isErrorMessage(userMessage) ? 'error' : 'success'}>
                {userMessage}
              </Alert>
            )}

            <div className="admin-form-grid">
              <TextField label="Ім'я" value={userDraft.name} onChange={(event) => updateDraft({ name: event.target.value })} />
              <TextField label="Прізвище" value={userDraft.lastName} onChange={(event) => updateDraft({ lastName: event.target.value })} />
              <TextField label="Телефон" value={userDraft.phoneNumber} onChange={(event) => updateDraft({ phoneNumber: event.target.value })} />
              <TextField label="Нікнейм" value={userDraft.nickname} onChange={(event) => updateDraft({ nickname: event.target.value })} />
              <TextField
                select
                label="Роль"
                value={userDraft.role}
                onChange={(event) => updateDraft({ role: event.target.value as UserRole })}
              >
                {Object.values(UserRole).map((role) => (
                  <MenuItem key={role} value={role}>{roleLabels[role]}</MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Статус"
                value={userDraft.status}
                onChange={(event) => updateDraft({ status: event.target.value as UserStatus })}
              >
                {Object.values(UserStatus).map((status) => (
                  <MenuItem key={status} value={status}>{statusUserLabels[status]}</MenuItem>
                ))}
              </TextField>
            </div>

            <div className="admin-actions">
              <Button variant="contained" onClick={onSaveUser} disabled={savingUser}>
                {savingUser ? 'Зберігаємо...' : 'Зберегти користувача'}
              </Button>
            </div>

            <AccountInviteBox user={selectedUser} />
          </>
        ) : (
          <div className="admin-order-detail admin-empty-detail">Виберіть користувача зі списку.</div>
        )}
      </article>
    </section>
  );
}

interface CreateOrderFormProps {
  userSearch: string;
  userOptions: AdminUser[];
  selectedUser: AdminUser | null;
  items: OrderItemDraft[];
  catalogItems: AdminCatalogItem[];
  knownCatalogItems: AdminCatalogItem[];
  newItemId: string;
  newItemSearch: string;
  delivery: DeliveryInformationData;
  userComment: string;
  comment: string;
  saving: boolean;
  message: string | null;
  isCreatingClient: boolean;
  newClientName: string;
  newClientLastName: string;
  newClientEmail: string;
  newClientPhone: string;
  creatingClient: boolean;
  onUserSearchChange: (value: string) => void;
  onSelectUser: (user: AdminUser | null) => void;
  onItemsChange: (value: OrderItemDraft[]) => void;
  onNewItemIdChange: (value: string) => void;
  onNewItemSearchChange: (value: string) => void;
  onKnownCatalogItemsChange: (value: AdminCatalogItem[]) => void;
  onDeliveryChange: (value: DeliveryInformationData) => void;
  onUserCommentChange: (value: string) => void;
  onCommentChange: (value: string) => void;
  onOpenCreateClient: (initialQuery: string) => void;
  onCancelCreateClient: () => void;
  onNewClientNameChange: (value: string) => void;
  onNewClientLastNameChange: (value: string) => void;
  onNewClientEmailChange: (value: string) => void;
  onNewClientPhoneChange: (value: string) => void;
  onCreateClientSubmit: () => void;
  onCancel: () => void;
  onSubmit: () => void;
}

function CreateOrderForm({
  userSearch,
  userOptions,
  selectedUser,
  items,
  catalogItems,
  knownCatalogItems,
  newItemId,
  newItemSearch,
  delivery,
  userComment,
  comment,
  saving,
  message,
  isCreatingClient,
  newClientName,
  newClientLastName,
  newClientEmail,
  newClientPhone,
  creatingClient,
  onUserSearchChange,
  onSelectUser,
  onItemsChange,
  onNewItemIdChange,
  onNewItemSearchChange,
  onKnownCatalogItemsChange,
  onDeliveryChange,
  onUserCommentChange,
  onCommentChange,
  onOpenCreateClient,
  onCancelCreateClient,
  onNewClientNameChange,
  onNewClientLastNameChange,
  onNewClientEmailChange,
  onNewClientPhoneChange,
  onCreateClientSubmit,
  onCancel,
  onSubmit,
}: CreateOrderFormProps) {
  const [selectedCatalogItem, setSelectedCatalogItem] = useState<AdminCatalogItem | null>(null);
  const [clientOrders, setClientOrders] = useState<AdminOrder[]>([]);
  const [clientOrdersLoading, setClientOrdersLoading] = useState(false);

  useEffect(() => {
    if (!selectedUser) {
      setClientOrders([]);
      return;
    }

    let ignore = false;
    setClientOrdersLoading(true);
    getOrdersByUser(selectedUser.id)
      .then((orders) => {
        if (!ignore) setClientOrders(orders);
      })
      .catch(() => {
        if (!ignore) setClientOrders([]);
      })
      .finally(() => {
        if (!ignore) setClientOrdersLoading(false);
      });

    return () => {
      ignore = true;
    };
  }, [selectedUser]);

  const addItem = () => {
    if (!newItemId || items.some((item) => item.itemId === newItemId)) return;
    onItemsChange([...items, { itemId: newItemId, count: '1' }]);
    setSelectedCatalogItem(null);
    onNewItemIdChange('');
    onNewItemSearchChange('');
  };
  const removeItem = (index: number) => onItemsChange(items.filter((_, itemIndex) => itemIndex !== index));

  const itemById = (itemId: string) => {
    const id = Number(itemId);
    return catalogItems.find((item) => item.id === id) ?? knownCatalogItems.find((item) => item.id === id);
  };

  const searchQuery = normalize(newItemSearch);
  const canShowCatalogMatches = searchQuery.length >= 4;
  const availableCatalogItems = canShowCatalogMatches
    ? catalogItems.filter(
      (item) =>
        !items.some((draft) => Number(draft.itemId) === item.id) &&
        item.stockCount > 0 &&
        normalize(`${item.name} ${item.id}`).includes(searchQuery)
    )
    : [];

  return (
    <article className="admin-order-detail">
      <header className="admin-order-header">
        <div>
          <p className="admin-kicker">Нове замовлення</p>
          <h2>Оформити для клієнта</h2>
        </div>
        <Button onClick={onCancel}>Скасувати</Button>
      </header>

      {message && <Alert severity={isErrorMessage(message) ? 'error' : 'success'}>{message}</Alert>}

      <section className="admin-section">
        <h3>Клієнт</h3>
        {isCreatingClient ? (
          <>
            <div className="admin-form-grid">
              <TextField label="Ім'я" value={newClientName} onChange={(event) => onNewClientNameChange(event.target.value)} required />
              <TextField label="Прізвище" value={newClientLastName} onChange={(event) => onNewClientLastNameChange(event.target.value)} required />
              <TextField label="Email (необов'язково)" value={newClientEmail} onChange={(event) => onNewClientEmailChange(event.target.value)} />
              <TextField label="Телефон" value={newClientPhone} onChange={(event) => onNewClientPhoneChange(event.target.value)} />
            </div>
            <div className="admin-actions">
              <Button variant="contained" onClick={onCreateClientSubmit} disabled={creatingClient}>
                {creatingClient ? 'Створюємо...' : 'Створити клієнта'}
              </Button>
              <Button onClick={onCancelCreateClient}>Скасувати</Button>
            </div>
          </>
        ) : (
          <>
            <Autocomplete
              options={userOptions}
              value={selectedUser}
              inputValue={userSearch}
              getOptionLabel={(option) => `${option.name} ${option.lastName} · ${option.email}`}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              noOptionsText="Нічого не знайдено"
              onInputChange={(_, value, reason) => {
                onUserSearchChange(value);
                if (reason === 'input') {
                  onSelectUser(null);
                }
              }}
              onChange={(_, value) => onSelectUser(value)}
              renderInput={(params) => (
                <TextField {...params} label="Пошук клієнта" helperText="Ім'я, прізвище або email" />
              )}
              fullWidth
            />
            <Button variant="text" onClick={() => onOpenCreateClient(userSearch)}>
              Клієнта немає в базі? Створити нового
            </Button>
          </>
        )}
      </section>

      {selectedUser && (
        <section className="admin-section">
          <h3>Замовлення клієнта {clientOrders.length ? `(${clientOrders.length})` : ''}</h3>
          {clientOrdersLoading ? (
            <CircularProgress size={24} />
          ) : clientOrders.length ? (
            <div className="admin-current-items">
              {clientOrders.map((order) => (
                <div key={order.id} className="admin-current-item admin-client-order-row">
                  <div className="admin-item-title">
                    <strong>#{order.id}</strong>
                    <span>{statusLabels[order.orderStatus]}</span>
                    <small>{formatDate(order.orderDate)}</small>
                  </div>
                  <small>
                    {order.items.map((item) => item.name).filter(Boolean).join(', ') || 'Без книг'}
                  </small>
                </div>
              ))}
            </div>
          ) : (
            <p className="admin-empty">У цього клієнта ще немає замовлень.</p>
          )}
        </section>
      )}

      <section className="admin-section">
        <h3>Книги в замовленні ({items.length})</h3>
        <div className="admin-current-items">
          {items.map((draft, index) => {
            const item = itemById(draft.itemId);
            return (
              <div key={`${draft.itemId}-${index}`} className="admin-current-item">
                <div className="admin-item-title">
                  <strong>{index + 1}.</strong>
                  {item?.name ? (
                    <Link to={`${ROUTES.DETAILS}/${draft.itemId}`} target="_blank" rel="noopener noreferrer">
                      {item.name}
                    </Link>
                  ) : (
                    <span>{`Товар #${draft.itemId}`}</span>
                  )}
                </div>
                <small>{money(item?.price ?? 0)}</small>
                <Button color="error" onClick={() => removeItem(index)}>Видалити</Button>
              </div>
            );
          })}
        </div>
        {items.length === 0 && <p className="admin-empty">Додайте хоча б одну книгу.</p>}
        <div className="admin-add-item-row">
          <Autocomplete
            options={availableCatalogItems}
            value={selectedCatalogItem}
            inputValue={newItemSearch}
            open={canShowCatalogMatches && !newItemId}
            getOptionLabel={(option) => `${option.name} · ${money(option.price)}`}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            noOptionsText="Нічого не знайдено"
            onInputChange={(_, value, reason) => {
              onNewItemSearchChange(value);
              if (reason === 'input') {
                onNewItemIdChange('');
                setSelectedCatalogItem(null);
              }
            }}
            onChange={(_, value) => {
              setSelectedCatalogItem(value);
              if (value) {
                onKnownCatalogItemsChange(
                  knownCatalogItems.some((item) => item.id === value.id)
                    ? knownCatalogItems
                    : [...knownCatalogItems, value]
                );
              }
              onNewItemIdChange(value ? String(value.id) : '');
              onNewItemSearchChange(value ? value.name : '');
            }}
            renderInput={(params) => (
              <TextField {...params} label="Пошук книги для додавання" helperText="Введіть щонайменше 4 символи" />
            )}
            fullWidth
          />
          <Button variant="outlined" onClick={addItem} disabled={!newItemId}>Додати</Button>
        </div>
      </section>

      <section className="admin-section">
        <h3>Доставка</h3>
        <div className="admin-form-grid">
          <TextField label="Ім'я отримувача" value={delivery.name} onChange={(event) => onDeliveryChange({ ...delivery, name: event.target.value })} required />
          <TextField label="Прізвище отримувача" value={delivery.lastName} onChange={(event) => onDeliveryChange({ ...delivery, lastName: event.target.value })} required />
          <TextField label="Телефон" value={delivery.phone} onChange={(event) => onDeliveryChange({ ...delivery, phone: event.target.value })} required />
          <TextField
            select
            label="Служба доставки"
            value={delivery.provider}
            SelectProps={{ renderValue: renderProviderValue }}
            onChange={(event) => {
              const provider = event.target.value as DeliveryProvider;
              onDeliveryChange(clearProviderDetails({
                ...delivery,
                provider,
                destination: provider === DeliveryProvider.Self ? DeliveryDestination.Address : DeliveryDestination.Department,
              }));
            }}
          >
            {Object.entries(providerLabel).map(([value, label]) => (
              <MenuItem key={value} value={value} className="provider-menu-item">
                <DeliveryProviderIcon provider={value} />
                {label}
              </MenuItem>
            ))}
          </TextField>
          {delivery.provider === DeliveryProvider.NovaPost ? (
            <NovaPoshtaAddressFields
              value={delivery}
              onChange={onDeliveryChange}
              destinationControl={(
                <TextField
                  select
                  label="Тип доставки"
                  value={delivery.destination}
                  onChange={(event) => onDeliveryChange(clearDeliveryTarget(delivery, event.target.value as DeliveryDestination))}
                >
                  {Object.entries(destinationLabel).map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          ) : delivery.provider === DeliveryProvider.Self ? (
            <SelfPickupFields value={delivery} onChange={onDeliveryChange} />
          ) : (
            <>
              <TextField
                select
                label="Тип доставки"
                value={delivery.destination}
                onChange={(event) => onDeliveryChange(clearDeliveryTarget(delivery, event.target.value as DeliveryDestination))}
              >
                {Object.entries(destinationLabel).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Місто"
                value={delivery.city}
                onChange={(event) => {
                  const city = event.target.value;
                  onDeliveryChange({ ...delivery, city, displayName: [city, delivery.number].filter(Boolean).join(', ') });
                }}
                required
              />
              <TextField
                label="Номер відділення, індекс або адреса"
                value={delivery.number}
                onChange={(event) => {
                  const number = event.target.value;
                  onDeliveryChange({ ...delivery, number, displayName: [delivery.city, number].filter(Boolean).join(', ') });
                }}
                required
              />
            </>
          )}
        </div>
      </section>

      <section className="admin-section">
        <h3>Коментарі</h3>
        <div className="admin-form-grid">
          <TextField
            label="Коментар клієнта"
            value={userComment}
            onChange={(event) => onUserCommentChange(event.target.value)}
            multiline
            minRows={2}
          />
          <TextField
            label="Внутрішній коментар"
            value={comment}
            onChange={(event) => onCommentChange(event.target.value)}
            multiline
            minRows={2}
          />
        </div>
      </section>

      <div className="admin-actions">
        <Button variant="contained" onClick={onSubmit} disabled={saving}>
          {saving ? 'Створюємо...' : 'Створити замовлення'}
        </Button>
      </div>
    </article>
  );
}

function OrderDetails({
  order,
  statusDraft,
  commentDraft,
  trackingDraft,
  itemsDraft,
  catalogItems,
  newItemId,
  newItemSearch,
  savingOrder,
  onStatusChange,
  onCommentChange,
  onTrackingChange,
  onItemsChange,
  onNewItemIdChange,
  onNewItemSearchChange,
  onStatusSave,
  onItemsSave,
}: OrderDetailsProps) {
  const [selectedCatalogItem, setSelectedCatalogItem] = useState<AdminCatalogItem | null>(null);
  const [knownCatalogItems, setKnownCatalogItems] = useState<AdminCatalogItem[]>([]);

  useEffect(() => {
    setSelectedCatalogItem(null);
    setKnownCatalogItems([]);
  }, [order.id]);

  const addItem = () => {
    if (!newItemId || itemsDraft.some((item) => item.itemId === newItemId)) return;
    onItemsChange([...itemsDraft, { itemId: newItemId, count: '1' }]);
    setSelectedCatalogItem(null);
    onNewItemIdChange('');
    onNewItemSearchChange('');
  };
  const removeItem = (index: number) => onItemsChange(itemsDraft.filter((_, itemIndex) => itemIndex !== index));

  const itemById = (itemId: string) => {
    const id = Number(itemId);
    return order.items.find((item) => item.itemId === id)
      ?? catalogItems.find((item) => item.id === id)
      ?? knownCatalogItems.find((item) => item.id === id);
  };

  const itemsDraftTotal = itemsDraft.reduce((sum, draft) => {
    const item = itemById(draft.itemId);
    return sum + (item?.price ?? 0) * (Number(draft.count) || 1);
  }, 0);

  const searchQuery = normalize(newItemSearch);
  const canShowCatalogMatches = searchQuery.length >= 4;
  const availableCatalogItems = canShowCatalogMatches
    ? catalogItems.filter(
      (item) =>
        !itemsDraft.some((draft) => Number(draft.itemId) === item.id) &&
        item.stockCount > 0 &&
        normalize(`${item.name} ${item.id}`).includes(searchQuery)
    )
    : [];
  return (
    <article className="admin-order-detail">
      <header className="admin-order-header">
        <div>
          <p className="admin-kicker">Замовлення</p>
          <h2>#{order.id}</h2>
          <p>{formatDate(order.orderDate)} · {money(order.totalPrice)}</p>
        </div>
        <span className={`admin-status status-${order.orderStatus}`}>{statusLabels[order.orderStatus]}</span>
      </header>

      <section className="admin-info-grid">
        <div>
          <h3>Клієнт</h3>
          <p>{order.userName} {order.userLastName}</p>
          <p>{order.userEmail}</p>
          <p>{order.userPhoneNumber}</p>
        </div>
        <div>
          <h3>Доставка</h3>
          <p>{order.userDeliveryInformation.displayName || `${order.userDeliveryInformation.city}, ${order.userDeliveryInformation.number}`}</p>
          <p>{order.userDeliveryInformation.phone}</p>
          <p>{String(order.userDeliveryInformation.provider)} · {String(order.userDeliveryInformation.destination)}</p>
        </div>
      </section>

      {order.userComment && <Alert severity="info">Коментар клієнта: {order.userComment}</Alert>}

      <section className="admin-section">
        <h3>Статус і супровід</h3>
        <div className="admin-form-grid">
          <TextField select label="Статус" value={statusDraft} onChange={(event) => onStatusChange(event.target.value as OrderStatus)}>
            {orderStatuses.map((status) => <MenuItem key={status} value={status}>{statusLabels[status]}</MenuItem>)}
          </TextField>
          <TextField label="ТТН / tracking" value={trackingDraft} onChange={(event) => onTrackingChange(event.target.value)} />
          <TextField
            label="Внутрішній коментар"
            value={commentDraft}
            onChange={(event) => onCommentChange(event.target.value)}
            multiline
            minRows={2}
          />
        </div>
        <Button variant="contained" onClick={onStatusSave} disabled={savingOrder}>
          {savingOrder ? 'Зберігаємо...' : 'Зберегти статус'}
        </Button>
      </section>

      <section className="admin-section">
        <h3>Книги в замовленні ({itemsDraft.length})</h3>
        <div className="admin-current-items">
          {itemsDraft.map((draft, index) => {
            const item = itemById(draft.itemId) as AdminOrderItem | AdminCatalogItem | undefined;
            const price = item?.price ?? 0;
            const thumbnailUrl = toAbsoluteUrl(item?.primaryImageUrl);
            return (
            <div key={`${draft.itemId}-${index}`} className="admin-current-item">
              {thumbnailUrl ? (
                <img className="admin-item-thumbnail" src={thumbnailUrl} alt="" width={40} height={56} />
              ) : (
                <div className="admin-item-thumbnail admin-item-thumbnail-placeholder" />
              )}
              <div className="admin-item-title">
                <strong>{index + 1}.</strong>
                {item?.name ? (
                  <Link to={`${ROUTES.DETAILS}/${draft.itemId}`} target="_blank" rel="noopener noreferrer">
                    {item.name}
                  </Link>
                ) : (
                  <span>{`Товар #${draft.itemId}`}</span>
                )}
              </div>
              <small>{money(price)}</small>
              <Button color="error" onClick={() => removeItem(index)}>Видалити</Button>
            </div>
          );
          })}
        </div>
        {itemsDraft.length === 0 && <p className="admin-empty">У замовленні поки немає книг.</p>}
        {itemsDraft.length > 0 && (
          <p className="admin-items-total"><strong>Разом: {money(itemsDraftTotal)}</strong></p>
        )}
        <div className="admin-add-item-row">
          <Autocomplete
            options={availableCatalogItems}
            value={selectedCatalogItem}
            inputValue={newItemSearch}
            open={canShowCatalogMatches && !newItemId}
            getOptionLabel={(option) => `${option.name} · ${money(option.price)}`}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            noOptionsText="Нічого не знайдено"
            onInputChange={(_, value, reason) => {
              onNewItemSearchChange(value);
              if (reason === 'input') {
                onNewItemIdChange('');
                setSelectedCatalogItem(null);
              }
            }}
            onChange={(_, value) => {
              setSelectedCatalogItem(value);
              if (value) {
                setKnownCatalogItems((current) =>
                  current.some((item) => item.id === value.id) ? current : [...current, value]
                );
              }
              onNewItemIdChange(value ? String(value.id) : '');
              onNewItemSearchChange(value ? value.name : '');
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Пошук книги для додавання"
                helperText="Введіть щонайменше 4 символи"
              />
            )}
            fullWidth
          />
          <Button variant="outlined" onClick={addItem} disabled={!newItemId}>Додати</Button>
        </div>
        <div className="admin-actions">
          <Button variant="contained" onClick={onItemsSave} disabled={savingOrder}>Зберегти склад</Button>
        </div>
      </section>
    </article>
  );
}

interface CollectionDraft {
  title: string;
  slug: string;
  description: string;
  isActive: boolean;
  items: AdminCollectionItemRequest[];
}

const emptyCollectionDraft: CollectionDraft = {
  title: '',
  slug: '',
  description: '',
  isActive: false,
  items: [],
};

const toCollectionDraft = (collection: AdminCollection): CollectionDraft => ({
  title: collection.title,
  slug: collection.slug,
  description: collection.description ?? '',
  isActive: collection.isActive,
  items: collection.items
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((item) => ({ bookId: item.book.id, sortOrder: item.sortOrder, comment: item.comment ?? '' })),
});

// Self-contained rather than threaded through AdminPage's own state like the other panels —
// this is a new, independent feature area, and adding another dozen props to the already very
// large parent component for it wasn't worth the coupling.
function AdminCollectionsPanel() {
  const [collections, setCollections] = useState<AdminCollection[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [draft, setDraft] = useState<CollectionDraft>(emptyCollectionDraft);
  const [message, setMessage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [bookSearch, setBookSearch] = useState('');
  const [bookOptions, setBookOptions] = useState<AdminCatalogItem[]>([]);
  const [knownBooks, setKnownBooks] = useState<Map<number, AdminCatalogItem>>(new Map());
  const [copiedSlug, setCopiedSlug] = useState<string | null>(null);

  const selectedCollection = useMemo(
    () => collections.find((collection) => collection.id === selectedId) ?? null,
    [collections, selectedId]
  );

  const loadCollections = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getAdminCollections();
      setCollections(response);
      setKnownBooks((previous) => {
        const next = new Map(previous);
        response.forEach((collection) =>
          collection.items.forEach((item) => {
            next.set(item.book.id, {
              id: item.book.id,
              name: item.book.name,
              stockCount: 0,
              price: 0,
            });
          })
        );
        return next;
      });
    } catch {
      setMessage('Не вдалося завантажити підбірки.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCollections();
  }, [loadCollections]);

  useEffect(() => {
    const query = bookSearch.trim();
    if (query.length < 3) {
      setBookOptions([]);
      return;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const response = await getCatalogItems(query, 0, 20);
        if (!cancelled) setBookOptions(response);
      } catch {
        if (!cancelled) setBookOptions([]);
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [bookSearch]);

  const handleSelect = (collection: AdminCollection) => {
    setSelectedId(collection.id);
    setDraft(toCollectionDraft(collection));
    setMessage(null);
  };

  const handleNew = () => {
    setSelectedId(null);
    setDraft(emptyCollectionDraft);
    setMessage(null);
  };

  const updateDraft = (patch: Partial<CollectionDraft>) => setDraft((current) => ({ ...current, ...patch }));

  const addBook = (item: AdminCatalogItem) => {
    if (draft.items.some((existing) => existing.bookId === item.id)) return;
    setKnownBooks((previous) => new Map(previous).set(item.id, item));
    updateDraft({
      items: [...draft.items, { bookId: item.id, sortOrder: draft.items.length, comment: '' }],
    });
    setBookSearch('');
    setBookOptions([]);
  };

  const removeBook = (bookId: number) => {
    updateDraft({
      items: draft.items
        .filter((item) => item.bookId !== bookId)
        .map((item, index) => ({ ...item, sortOrder: index })),
    });
  };

  const moveBook = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= draft.items.length) return;
    const next = draft.items.slice();
    [next[index], next[target]] = [next[target], next[index]];
    updateDraft({ items: next.map((item, i) => ({ ...item, sortOrder: i })) });
  };

  const updateComment = (bookId: number, comment: string) => {
    updateDraft({ items: draft.items.map((item) => (item.bookId === bookId ? { ...item, comment } : item)) });
  };

  const handleSave = async () => {
    if (!draft.title.trim()) {
      setMessage('Вкажіть назву підбірки.');
      return;
    }
    if (!draft.slug.trim()) {
      setMessage('Вкажіть слаг для посилання (латиницею).');
      return;
    }

    setSaving(true);
    setMessage(null);
    try {
      const request = {
        title: draft.title.trim(),
        slug: draft.slug.trim(),
        description: draft.description.trim() || undefined,
        isActive: draft.isActive,
        sortOrder: selectedCollection?.sortOrder ?? collections.length,
        items: draft.items,
      };

      if (selectedCollection) {
        await updateAdminCollection(selectedCollection.id, request);
        setMessage('Підбірку оновлено.');
      } else {
        const created = await createAdminCollection(request);
        setSelectedId(created.id);
        setMessage('Підбірку створено.');
      }
      await loadCollections();
    } catch {
      setMessage('Не вдалося зберегти підбірку — можливо, такий слаг вже зайнятий.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedCollection) return;
    setSaving(true);
    try {
      await deleteAdminCollection(selectedCollection.id);
      setMessage('Підбірку видалено.');
      handleNew();
      await loadCollections();
    } catch {
      setMessage('Не вдалося видалити підбірку.');
    } finally {
      setSaving(false);
    }
  };

  const shareUrl = (slug: string) => `${window.location.origin}${ROUTES.LIST}/${slug}`;

  const handleCopyLink = async (slug: string) => {
    try {
      await navigator.clipboard.writeText(shareUrl(slug));
      setCopiedSlug(slug);
      setTimeout(() => setCopiedSlug((current) => (current === slug ? null : current)), 2000);
    } catch {
      setMessage('Не вдалося скопіювати посилання.');
    }
  };

  return (
    <section className="admin-books-layout">
      <aside className="admin-book-list">
        <div className="admin-book-toolbar">
          <Button variant="contained" onClick={handleNew}>Нова підбірка</Button>
        </div>

        {loading ? (
          <CircularProgress />
        ) : collections.length ? (
          <div className="admin-book-rows">
            {collections.map((collection) => (
              <button
                type="button"
                key={collection.id}
                className={`admin-order-row ${selectedId === collection.id ? 'selected' : ''}`}
                onClick={() => handleSelect(collection)}
              >
                <div className="admin-order-row-main">
                  <strong>{collection.title}</strong>
                  <span>{collection.isActive ? 'На головній' : 'Тільки за лінком'}</span>
                </div>
                <small>{collection.items.length} книг(и) · /{collection.slug}</small>
              </button>
            ))}
          </div>
        ) : (
          <p className="admin-empty">Підбірок ще немає.</p>
        )}
      </aside>

      <article className="admin-book-editor">
        <header className="admin-order-header">
          <div>
            <p className="admin-kicker">{selectedCollection ? `Підбірка #${selectedCollection.id}` : 'Нова підбірка'}</p>
            <h2>{selectedCollection ? selectedCollection.title : 'Створити підбірку'}</h2>
          </div>
        </header>

        {message && <Alert severity={isErrorMessage(message) ? 'error' : 'success'}>{message}</Alert>}

        <div className="admin-book-form">
          <TextField label="Назва" value={draft.title} onChange={(event) => updateDraft({ title: event.target.value })} />
          <TextField
            label="Слаг (для посилання)"
            value={draft.slug}
            onChange={(event) => updateDraft({ slug: event.target.value.trim() })}
            helperText="Латиницею, без пробілів, напр. moya-pidbirka"
          />
          <TextField
            label="Опис"
            value={draft.description}
            onChange={(event) => updateDraft({ description: event.target.value })}
            multiline
            minRows={2}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={draft.isActive}
                onChange={(event) => updateDraft({ isActive: event.target.checked })}
              />
            }
            label="Показувати на головній сторінці"
          />
        </div>

        {draft.slug.trim() && (
          <div className="admin-actions">
            <TextField
              size="small"
              value={shareUrl(draft.slug.trim())}
              InputProps={{ readOnly: true }}
              sx={{ flex: 1, minWidth: 260 }}
            />
            <Button variant="outlined" onClick={() => handleCopyLink(draft.slug.trim())}>
              {copiedSlug === draft.slug.trim() ? 'Скопійовано!' : 'Скопіювати посилання'}
            </Button>
          </div>
        )}

        <div className="admin-actions">
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Зберігаємо...' : selectedCollection ? 'Зберегти підбірку' : 'Створити підбірку'}
          </Button>
          {selectedCollection && (
            <Button color="error" variant="outlined" onClick={handleDelete} disabled={saving}>
              Видалити підбірку
            </Button>
          )}
        </div>

        <section className="admin-section">
          <h3>Книги в підбірці ({draft.items.length})</h3>
          <div className="admin-current-items">
            {draft.items.map((item, index) => {
              const known = knownBooks.get(item.bookId);
              return (
                <div className="admin-current-item" key={item.bookId}>
                  <div className="admin-item-title">
                    <strong>{index + 1}.</strong>
                    <span>{known?.name ?? `Книга #${item.bookId}`}</span>
                  </div>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Короткий коментар — чому радите цю книгу"
                    value={item.comment ?? ''}
                    onChange={(event) => updateComment(item.bookId, event.target.value)}
                    multiline
                    minRows={1}
                  />
                  <Button size="small" onClick={() => moveBook(index, -1)} disabled={index === 0}>Вище</Button>
                  <Button size="small" onClick={() => moveBook(index, 1)} disabled={index === draft.items.length - 1}>
                    Нижче
                  </Button>
                  <Button size="small" color="error" onClick={() => removeBook(item.bookId)}>Видалити</Button>
                </div>
              );
            })}
          </div>
          {draft.items.length === 0 && <p className="admin-empty">Додайте хоча б одну книгу.</p>}

          <div className="admin-add-item-row">
            <Autocomplete
              options={bookOptions}
              inputValue={bookSearch}
              value={null}
              getOptionLabel={(option) => option.name}
              noOptionsText={bookSearch.trim().length < 3 ? 'Введіть щонайменше 3 символи' : 'Нічого не знайдено'}
              onInputChange={(_, value) => setBookSearch(value)}
              onChange={(_, value) => value && addBook(value)}
              renderInput={(params) => <TextField {...params} label="Додати книгу до підбірки" />}
              fullWidth
            />
          </div>
        </section>
      </article>
    </section>
  );
}

const waitlistSourceLabels: Record<WaitlistSource, string> = {
  [WaitlistSource.Site]: 'Сайт',
  [WaitlistSource.Instagram]: 'Instagram',
  [WaitlistSource.Other]: 'Інше',
};

interface WaitlistEntryDraft {
  source: WaitlistSource;
  contactName: string;
  contact: string;
  note: string;
}

const emptyWaitlistEntryDraft: WaitlistEntryDraft = {
  source: WaitlistSource.Instagram,
  contactName: '',
  contact: '',
  note: '',
};

const toWaitlistEntryDraft = (entry: AdminWaitlistEntry): WaitlistEntryDraft => ({
  source: entry.source,
  contactName: entry.userId ? '' : entry.name,
  contact: entry.contact ?? '',
  note: entry.note ?? '',
});

const toWaitlistEntryRequest = (draft: WaitlistEntryDraft) => ({
  source: draft.source,
  contactName: draft.contactName.trim() || undefined,
  contact: draft.contact.trim() || undefined,
  note: draft.note.trim() || undefined,
});

const instagramUrl = (contact: string) => {
  const handle = contact.trim().replace(/^@/, '');
  return /^[\w.]+$/.test(handle) ? `https://instagram.com/${handle}` : null;
};

// Self-contained like AdminCollectionsPanel: the queue is an independent feature area.
// Every change (add / move / edit / remove) is saved immediately and the server returns the
// whole updated queue, so what the admin sees is always the real order.
function AdminWaitlistPanel() {
  const [queues, setQueues] = useState<AdminWaitlistItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<AdminWaitlistItem | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [newEntry, setNewEntry] = useState<WaitlistEntryDraft>(emptyWaitlistEntryDraft);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState<WaitlistEntryDraft>(emptyWaitlistEntryDraft);
  const [bookSearch, setBookSearch] = useState('');
  const [bookOptions, setBookOptions] = useState<AdminCatalogItem[]>([]);

  const loadQueues = useCallback(async () => {
    setLoading(true);
    try {
      setQueues(await getAdminWaitlists());
    } catch {
      setMessage('Не вдалося завантажити черги.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadQueues();
  }, [loadQueues]);

  useEffect(() => {
    const query = bookSearch.trim();
    if (query.length < 3) {
      setBookOptions([]);
      return;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const response = await getCatalogItems(query, 0, 20);
        if (!cancelled) setBookOptions(response);
      } catch {
        if (!cancelled) setBookOptions([]);
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [bookSearch]);

  // Keep the left-hand list in sync with the queue the server just returned.
  const applyQueue = (queue: AdminWaitlistItem) => {
    setSelected(queue);
    setQueues((current) => {
      const others = current.filter((item) => item.itemId !== queue.itemId);
      const next = queue.entries.length ? [...others, queue] : others;
      return next.sort((a, b) => a.itemName.localeCompare(b.itemName, 'uk'));
    });
  };

  const runAction = async (action: () => Promise<AdminWaitlistItem>, successMessage: string | null) => {
    setSaving(true);
    setMessage(null);
    try {
      applyQueue(await action());
      if (successMessage) setMessage(successMessage);
      return true;
    } catch (error) {
      setMessage(error instanceof Error && error.message ? `Не вдалося: ${error.message}` : 'Не вдалося зберегти зміни.');
      if (selected) {
        try {
          applyQueue(await getAdminWaitlist(selected.itemId));
        } catch {
          // The original error message is already shown.
        }
      }
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSelect = (queue: AdminWaitlistItem) => {
    setSelected(queue);
    setEditingId(null);
    setMessage(null);
  };

  const handleOpenBook = async (item: AdminCatalogItem) => {
    setBookSearch('');
    setBookOptions([]);
    setEditingId(null);
    setMessage(null);
    try {
      setSelected(await getAdminWaitlist(item.id));
    } catch {
      setMessage('Не вдалося відкрити чергу для цієї книги.');
    }
  };

  const moveEntry = (index: number, target: number) => {
    if (!selected || target < 0 || target >= selected.entries.length || target === index) return;
    const ids = selected.entries.map((entry) => entry.id);
    const [moved] = ids.splice(index, 1);
    ids.splice(target, 0, moved);
    runAction(() => reorderAdminWaitlist(selected.itemId, ids), null);
  };

  const handleAdd = async () => {
    if (!selected) return;
    if (!newEntry.contactName.trim() && !newEntry.contact.trim()) {
      setMessage("Вкажіть ім'я або контакт людини.");
      return;
    }

    const added = await runAction(
      () => addAdminWaitlistEntry(selected.itemId, toWaitlistEntryRequest(newEntry)),
      'Додано в кінець черги.'
    );
    if (added) setNewEntry((current) => ({ ...emptyWaitlistEntryDraft, source: current.source }));
  };

  const startEdit = (entry: AdminWaitlistEntry) => {
    setEditingId(entry.id);
    setEditDraft(toWaitlistEntryDraft(entry));
  };

  const handleSaveEdit = async (entry: AdminWaitlistEntry) => {
    const saved = await runAction(
      () => updateAdminWaitlistEntry(entry.id, toWaitlistEntryRequest(editDraft)),
      'Збережено.'
    );
    if (saved) setEditingId(null);
  };

  const handleRemove = (entry: AdminWaitlistEntry) => {
    if (!window.confirm(`Прибрати «${entry.name || entry.contact}» з черги?`)) return;
    runAction(() => removeAdminWaitlistEntry(entry.id), 'Прибрано з черги.');
  };

  const renderContact = (entry: AdminWaitlistEntry) => {
    const parts: React.ReactNode[] = [];
    if (entry.contact) {
      const url = entry.source === WaitlistSource.Instagram ? instagramUrl(entry.contact) : null;
      parts.push(
        url ? (
          <a key="contact" href={url} target="_blank" rel="noreferrer">
            {entry.contact}
          </a>
        ) : (
          <span key="contact">{entry.contact}</span>
        )
      );
    }
    if (entry.email) parts.push(<span key="email">{entry.email}</span>);
    if (entry.phone) parts.push(<span key="phone">{entry.phone}</span>);
    return parts.length ? <div className="admin-waitlist-contacts">{parts}</div> : null;
  };

  return (
    <section className="admin-books-layout">
      <aside className="admin-book-list">
        <div className="admin-book-toolbar">
          <Autocomplete
            options={bookOptions}
            inputValue={bookSearch}
            value={null}
            getOptionLabel={(option) => option.name}
            noOptionsText={bookSearch.trim().length < 3 ? 'Введіть щонайменше 3 символи' : 'Нічого не знайдено'}
            onInputChange={(_, value) => setBookSearch(value)}
            onChange={(_, value) => value && handleOpenBook(value)}
            renderInput={(params) => <TextField {...params} label="Знайти книгу" size="small" />}
            fullWidth
          />
          <Button variant="outlined" onClick={loadQueues} disabled={loading}>
            Оновити
          </Button>
        </div>

        {loading ? (
          <CircularProgress />
        ) : queues.length ? (
          <div className="admin-book-rows">
            {queues.map((queue) => (
              <button
                type="button"
                key={queue.itemId}
                className={`admin-order-row ${selected?.itemId === queue.itemId ? 'selected' : ''}`}
                onClick={() => handleSelect(queue)}
              >
                <div className="admin-order-row-main">
                  <strong>{queue.itemName}</strong>
                  <span>{queue.entries.length} в черзі</span>
                </div>
                <small>
                  Наступна: {queue.entries[0]?.name || queue.entries[0]?.contact}
                  {queue.stockCount > 0 && ` · в наявності ${queue.stockCount}`}
                </small>
              </button>
            ))}
          </div>
        ) : (
          <p className="admin-empty">Поки ніхто не стоїть у черзі. Знайдіть книгу вище, щоб додати людину вручну.</p>
        )}
      </aside>

      <article className="admin-book-editor">
        {!selected ? (
          <p className="admin-empty">Оберіть книгу зліва або знайдіть її через пошук.</p>
        ) : (
          <>
            <header className="admin-order-header">
              <div>
                <p className="admin-kicker">Черга · книга #{selected.itemId}</p>
                <h2>{selected.itemName}</h2>
              </div>
              <span className="admin-waitlist-stock">
                {selected.stockCount > 0 ? `В наявності: ${selected.stockCount}` : 'Немає в наявності'}
              </span>
            </header>

            {message && <Alert severity={isErrorMessage(message) ? 'error' : 'success'}>{message}</Alert>}

            <section className="admin-section">
              <h3>Хто в черзі ({selected.entries.length})</h3>
              {selected.entries.length === 0 && <p className="admin-empty">Черга порожня.</p>}
              <div className="admin-current-items">
                {selected.entries.map((entry, index) => (
                  <div className="admin-waitlist-entry" key={entry.id}>
                    <strong className="admin-waitlist-position">{index + 1}</strong>

                    {editingId === entry.id ? (
                      <div className="admin-waitlist-edit">
                        {!entry.userId && (
                          <>
                            <TextField
                              size="small"
                              label="Ім'я"
                              value={editDraft.contactName}
                              onChange={(event) => setEditDraft({ ...editDraft, contactName: event.target.value })}
                            />
                            <TextField
                              size="small"
                              label="Контакт"
                              value={editDraft.contact}
                              onChange={(event) => setEditDraft({ ...editDraft, contact: event.target.value })}
                            />
                            <TextField
                              select
                              size="small"
                              label="Звідки"
                              value={editDraft.source}
                              onChange={(event) => setEditDraft({ ...editDraft, source: event.target.value as WaitlistSource })}
                            >
                              <MenuItem value={WaitlistSource.Instagram}>Instagram</MenuItem>
                              <MenuItem value={WaitlistSource.Other}>Інше</MenuItem>
                            </TextField>
                          </>
                        )}
                        <TextField
                          size="small"
                          label="Нотатка"
                          value={editDraft.note}
                          onChange={(event) => setEditDraft({ ...editDraft, note: event.target.value })}
                          multiline
                        />
                        <div className="admin-actions">
                          <Button size="small" variant="contained" onClick={() => handleSaveEdit(entry)} disabled={saving}>
                            Зберегти
                          </Button>
                          <Button size="small" onClick={() => setEditingId(null)} disabled={saving}>
                            Скасувати
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="admin-waitlist-info">
                        <div className="admin-item-title">
                          <span>{entry.name || entry.contact}</span>
                          <span className={`admin-waitlist-source source-${entry.source.toLowerCase()}`}>
                            {waitlistSourceLabels[entry.source]}
                          </span>
                        </div>
                        {renderContact(entry)}
                        {entry.note && <small className="admin-waitlist-note">{entry.note}</small>}
                        {/* Id grows with every new entry and never changes on reorder, so it shows the original
                            join order even for entries whose CreatedAt predates the column (set at migration time). */}
                        <small>
                          ID {entry.id} · з {formatDate(entry.createdAt)}
                        </small>
                      </div>
                    )}

                    {editingId !== entry.id && (
                      <div className="admin-waitlist-buttons">
                        <Button size="small" onClick={() => moveEntry(index, index - 1)} disabled={saving || index === 0}>
                          Вище
                        </Button>
                        <Button
                          size="small"
                          onClick={() => moveEntry(index, index + 1)}
                          disabled={saving || index === selected.entries.length - 1}
                        >
                          Нижче
                        </Button>
                        <Button size="small" onClick={() => startEdit(entry)} disabled={saving}>
                          Редагувати
                        </Button>
                        <Button size="small" color="error" onClick={() => handleRemove(entry)} disabled={saving}>
                          Прибрати
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>

            <section className="admin-section">
              <h3>Додати вручну</h3>
              <div className="admin-book-form">
                <TextField
                  label="Ім'я"
                  value={newEntry.contactName}
                  onChange={(event) => setNewEntry({ ...newEntry, contactName: event.target.value })}
                />
                <TextField
                  label="Контакт"
                  placeholder="@nickname або телефон"
                  value={newEntry.contact}
                  onChange={(event) => setNewEntry({ ...newEntry, contact: event.target.value })}
                />
                <TextField
                  select
                  label="Звідки"
                  value={newEntry.source}
                  onChange={(event) => setNewEntry({ ...newEntry, source: event.target.value as WaitlistSource })}
                >
                  <MenuItem value={WaitlistSource.Instagram}>Instagram</MenuItem>
                  <MenuItem value={WaitlistSource.Other}>Інше</MenuItem>
                </TextField>
                <TextField
                  label="Нотатка"
                  value={newEntry.note}
                  onChange={(event) => setNewEntry({ ...newEntry, note: event.target.value })}
                  multiline
                />
              </div>
              <div className="admin-actions">
                <Button variant="contained" onClick={handleAdd} disabled={saving}>
                  {saving ? 'Зберігаємо...' : 'Додати в кінець черги'}
                </Button>
              </div>
            </section>
          </>
        )}
      </article>
    </section>
  );
}

export default AdminPage;
